import React, { useState, useEffect, useRef } from 'react';
import './DataTable.css'; // Custom CSS file for additional styling
import { Button, Dropdown, DropdownMenu, DropdownToggle, DropdownItem } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import apiRequest from '../../api';

const checkTextOverflow = (text, container) => {
  if (!container) return false;

  // Get the computed style of the actual cell
  const computedStyle = window.getComputedStyle(container);

  // Create a temporary span element
  const span = document.createElement('span');
  span.style.visibility = 'hidden';
  span.style.position = 'absolute';
  span.style.whiteSpace = 'nowrap';
  // Copy the computed font styles from the cell
  span.style.fontSize = computedStyle.fontSize;
  span.style.fontFamily = computedStyle.fontFamily;
  span.style.fontWeight = computedStyle.fontWeight;
  span.style.letterSpacing = computedStyle.letterSpacing;
  span.innerText = text;
  document.body.appendChild(span);

  // Get the width of the text and container
  const textWidth = span.offsetWidth;
  const containerWidth = container.offsetWidth;

  // Clean up
  document.body.removeChild(span);

  // Return true if text width is greater than container width
  return textWidth > containerWidth;
};

const DataTable = ({ data, columns, visibleRows = data.length, onEdit = () => { }, fetchReservations, editable = true, ...rest }) => {

  const selectedLanguageRef = useRef(localStorage.getItem('Language') || 'EN');
  useEffect(() => {
    // Handle changes from other tabs/windows and local changes
    const handleLanguageChange = (event) => {
      selectedLanguageRef.current = event.detail['selectedLanguage'];
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' });
  const [sortedData, setSortedData] = useState([...data]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRefs = useRef({});
  const { pathname } = useLocation()
  const tableRef = useRef(null);
  const cellRefs = useRef({});

  useEffect(() => {
    // Update sortedData whenever data (filteredData) changes
    setSortedData([...data]);
  }, [data]);


  const sortData = (column) => {
    let direction = 'ascending';
    if (sortConfig.key === column && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    let sortedArray;

    if (column === "username") {
      sortedArray = [...data].sort((a, b) => {
        return direction === "ascending"
          ? a.username.toLowerCase().localeCompare(b.username.toLowerCase())
          : b.username.toLowerCase().localeCompare(a.username.toLowerCase());
      });
    } else {
      sortedArray = [...data].sort((a, b) => {
        if (a[column] < b[column]) {
          return direction === 'ascending' ? -1 : 1;
        }
        if (a[column] > b[column]) {
          return direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }


    setSortConfig({ key: column, direction });
    setSortedData(sortedArray);
  };

  const onDelete = async (row) => {
    try {
      const token = sessionStorage.getItem("jwt"); // Retrieve JWT token from localStorage
      const reservationCode = String(row.id).replace(/"/g, ''); // Remove quotes if they exist
      console.log("reservationCode:", reservationCode);
      const response = await apiRequest(`/api/reservations/deleteReservation?reservationCode=${reservationCode}`, 'POST', null, token)

      //console.log("Delete results:", response);
      fetchReservations();
      setSelectedRowIndex(null);
    } catch (error) {
      window.alert("Error deleting reservation:" + error.message);
    }
  };

  const onDeleteUser = async (row) => {
    try {
      const token = sessionStorage.getItem("jwt"); // Retrieve JWT token from localStorage
      const username = String(row.username).replace(/"/g, ''); // Remove quotes if they exist
      console.log("username that is deleting:", username);
      const response = await apiRequest(`/api/users/deleteUser?username=${username}`, 'POST', null, token)

      //console.log("Delete results:", response);
      fetchReservations();
      setSelectedRowIndex(null);
    } catch (error) {
      window.alert("Error deleting user: " + error.message);
    }
  };
  const onDeleteLot = async (row) => {
    try {
      const token = sessionStorage.getItem("jwt"); // Retrieve JWT token from localStorage
      const lot = String(row.lot).replace(/"/g, ''); // Remove quotes if they exist
      console.log("lot that is deleting:", lot);
      const response = await apiRequest(`/api/parking_lots/delete_lot?parkingLot=${lot}`, 'POST', null, token)


      //console.log("Delete results:", response);
      fetchReservations();
      setSelectedRowIndex(null);
    } catch (error) {
      window.alert("Error deleting lot:" + error.message);
    }
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setSelectedRowIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (id) => {

    if (dropdownOpen === id) {
      setDropdownOpen(null);
      setSelectedRowIndex(null);
    } else {
      setDropdownOpen(id);
      setSelectedRowIndex(id);
    }
  };
  useEffect(() => {
    if (dropdownOpen !== null) { // Only run when a dropdown is open
      //console.log(`Before Adjustment - Dropdown ${dropdownOpen}:`, dropdownRefs.current[dropdownOpen]?.getBoundingClientRect().bottom);
      setTimeout(() => {  // Small delay to allow rendering to settle
        requestAnimationFrame(() => { // Ensures update happens after reflow
          const dropdownRef = dropdownRefs.current[dropdownOpen];
          if (!dropdownRef) return;

          const menuElement = dropdownRef.querySelector('.dropdown-menu');
          if (!menuElement) return;

          const rect = menuElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          //console.log(`Final Adjustment - Dropdown ${dropdownOpen} rect.bottom:`, rect.bottom, `Viewport Height:`, viewportHeight);

          if (rect.bottom > viewportHeight) {
            console.log("Dropdown going beyond viewport - moving up");
            menuElement.style.position = 'absolute';
            menuElement.style.top = `-${rect.height}px`;
            menuElement.style.bottom = 'auto';
          } else {
            console.log("Dropdown fits - keeping default position");
            menuElement.style.position = '';
            menuElement.style.top = '';
            menuElement.style.bottom = '';
          }
        });
      }, 50);
    }
  }, [dropdownOpen]);
  // Helper function to format the date to "DD.MM.YYYY"
  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0'); // Add leading zero if day is a single digit
    const month = (d.getMonth() + 1).toString().padStart(2, '0'); // Get the month (zero-indexed), add leading zero if needed
    const year = d.getFullYear(); // Get the full year

    return `${day}.${month}.${year}`; // Return the formatted date
  };

  // Helper function to format the time to "HH:mm - HH:mm"
  const formatTime = (startDate, endDate) => {
    const formatTimeString = (date) => {
      const d = new Date(date);
      const hours = d.getHours().toString().padStart(2, '0'); // Get the hours and pad to 2 digits
      const minutes = d.getMinutes().toString().padStart(2, '0'); // Get the minutes and pad to 2 digits
      return `${hours}:${minutes}`;
    };

    const startTime = formatTimeString(startDate);
    const endTime = formatTimeString(endDate);

    return `${startTime} - ${endTime}`;
  };
  useEffect(() => {
    const handleResize = () => {
      // Force a re-render to update scroll status
      setForceUpdate(prev => !prev);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [forceUpdate, setForceUpdate] = useState(false);
  const needsScroll = (text, columnKey) => {
    if (!text) return false;
    const container = cellRefs.current[`${columnKey}`];
    return checkTextOverflow(text.toString(), container);
  };

  const shouldAdjustDropdown = pathname === "/admin/users" && data.length === 4;
  return (
    <div style={{ overflow: "auto" }} >
      <div ref={tableRef} className="table-container"  {...rest} >
        {/* Table Header */}
        <div className="table-header">
          {columns.map((col, index) => (
            <div
              key={index}
              className="header-cell"
              onClick={
                // Disable the function for "username" only on the "admin/history" page
                (pathname.includes('history') && col.key === 'name')
                  ? undefined  // Do not run for "username" on the history page
                  : // Otherwise, run for all other columns except "Name", "password", and "email"
                  (col.title !== 'Name' && col.key !== 'password' && col.key !== 'email')
                    ? () => sortData(col.key)
                    : undefined
              }
            >
              <div className="columns-container" style={{ display: 'flex', alignItems: 'center', lineHeight: '1'}}>
                {/* Hide sort icons for "username" column on the "admin/history" page */}
                {!(pathname.includes('history') && col.key === 'name') &&
                  (col.title !== 'Name' && col.key !== 'password' && col.key !== 'email') && (
                    <span className="sort-icons">
                      <span
                        className={`up-arrow mx-2 ${sortConfig.key === col.key && sortConfig.direction === 'ascending' ? 'active' : ''}`}
                        style={{ marginBottom: '1px' }}
                      >
                        ▲
                      </span>
                      <span
                        className={`down-arrow mx-2 ${sortConfig.key === col.key && sortConfig.direction === 'descending' ? 'active' : ''}`}
                      style={{ marginTop: '-2px' }}
                      >
                        ▼
                      </span>
                    </span>
                  )}
                <div>{col.title}</div>
              </div>

            </div>
          ))}
        </div>

        {/* Table Body */}
        <div className="scrollbar" id="style-9">
          <div className="force-overflow">
            <div className="table-body" style={{ position: visibleRows <= 3 ? "unset" : "unset" }} >
              {sortedData.slice(0, visibleRows).map((row, rowIndex) => (
                <Dropdown
                  isOpen={editable ? dropdownOpen === rowIndex : null}
                  toggle={() => toggleDropdown(rowIndex)}
                  style={{ position: (visibleRows <= 3 || shouldAdjustDropdown )? "unset" : "relative" }}
                  container="body"
                  ref={(el) => {
                    if (el && el.containerRef && el.containerRef.current && !dropdownRefs.current[rowIndex]) {
                      dropdownRefs.current[rowIndex] = el.containerRef.current;
                      //console.log(`Set ref for rowIndex ${rowIndex}:`, el.containerRef.current);
                    }
                  }}
                >
                  <DropdownToggle
                    style={{
                      background: "transparent",
                      color: "rgba(13, 48, 114, 1)",
                      width: "100%",
                      border: "none"
                    }}
                    className='table-dropdown'
                  >

                    <div
                      key={rowIndex}
                      className={`row m-0 default-row ${selectedRowIndex === rowIndex ? 'selected-row' : ''}`}


                      style={{ position: "relative" }}
                    >
                      {columns.map((col, colIndex) => (
                        <div 
                          key={`${rowIndex}-${col.key}`}
                          className={`cell ${needsScroll(row[col.key], `${rowIndex}-${col.key}`) ? 'needs-scroll' : ''}`}
                          ref={el => cellRefs.current[`${rowIndex}-${col.key}`] = el}
                        >
                          {col.key === 'startDate' ? (
                            <>
                              {formatDate(row.startDate)}
                              <br />
                              {formatTime(row.startDate, row.endDate)}
                            </>
                          ) : (
                            <div className="scroll-container">
                              <div className="scroll-text">
                                {row[col.key]}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                    </div>
                  </DropdownToggle>
                  {pathname === "/admin/users" ?
                    <DropdownMenu end className={visibleRows <= 3 ? "single-column" : "dropdown-container-2"}  >
                      <DropdownItem className="dropdown-btn" onClick={() => onEdit(row)} >{selectedLanguageRef.current === 'EN' ? "Edit" : "Izmeni"}</DropdownItem>
                      <div className="divider mx-2  mt-2 " />
                      <DropdownItem className="dropdown-btn mt-2  " onClick={() => onDeleteUser(row)} >{selectedLanguageRef.current === 'EN' ? "Delete" : "Ukloni"}</DropdownItem>


                    </DropdownMenu>
                    :
                    pathname === "/admin/lots" ?
                      <DropdownMenu right className={" lots-dropdown-container"}  >
                        <DropdownItem className="dropdown-btn mb-2 " onClick={() => onDeleteLot(row)} >{selectedLanguageRef.current === 'EN' ? "Delete" : "Ukloni"}</DropdownItem>
                      </DropdownMenu>
                      :
                      <DropdownMenu end className={visibleRows <= 3 ? "single-column" : "dropdown-container-2"}  >
                        <DropdownItem className="dropdown-btn  " onClick={() => onEdit(row)} >{selectedLanguageRef.current === 'EN' ? "Edit" : "Izmeni"}</DropdownItem>
                        <div className="divider mx-2 mt-2 " />
                        <DropdownItem className="dropdown-btn mt-2 " onClick={() => onDelete(row)}>{selectedLanguageRef.current === 'EN' ? "Delete" : "Ukloni"}</DropdownItem>


                      </DropdownMenu>
                  }

                </Dropdown>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
